import { useUser } from "@clerk/clerk-react";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { createUser, onboardUser as onboardUserApi } from "@/api/users";
import { termsLabelFor } from "@/constants/legal";
import { markSignUpPending, PAGE_VIEW_STATE, signUpMethodFor } from "@/lib/analytics";
import { ROLES } from "@/lib/roles";
import { userFacingErrorMessage } from "@/utils/userFacingError-helper";

export const POST_ONBOARDING_REDIRECT_URL = "/";
export const NEW_ACCOUNT_STORAGE_KEY = "gfg_new_account";

interface UseOnboardingSubmitProps {
  formData: {
    firstName: string;
    lastName: string;
    orgName: string;
    avatarFile: File | null;
    invitedEmails: string[];
    /** The Platform Terms version accepted on the profile step. Null until the box is ticked. */
    termsVersion: string | null;
  };
  setSubmitting: (isSubmitting: boolean) => void;
  setError: (error: string | null) => void;
  clearPersistedState: () => void;
  /** The wizard's persisted record that setup already created this client's organization. */
  hasOrganization: boolean;
  markOrganizationCreated: () => void;
}

export function useOnboardingSubmit({
  formData,
  setSubmitting,
  setError,
  clearPersistedState,
  hasOrganization,
  markOrganizationCreated,
}: UseOnboardingSubmitProps) {
  const { user } = useUser();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  /**
   * Did WE create the organization during this wizard run?
   *
   * `alreadyOnboarded` means "this user already has a group", which used to have exactly one cause:
   * an invited user who landed on /onboarding by mistake, and who should be sent straight to the
   * app. Since the setup step now creates the organization for BOTH plans, that flag has a second
   * cause — us, moments ago — and treating the two the same would skip the avatar upload and every
   * team invitation for anyone who got that far.
   */
  const didOnboardHere = useRef(false);

  /**
   * The same question as `didOnboardHere`, asked so it survives a remount.
   *
   * The ref alone is empty again after one, and every later answer is then wrong: `handleFinish`
   * reads `alreadyOnboarded` off the backend, concludes the client must be an invited user, and
   * navigates to the dashboard without uploading the avatar or sending a single invitation.
   */
  // A function, not a computed value: `runSetup` awaits `ensureOrganization` and calls
  // `handleFinish` in the same tick, so the ref must be read when it is ASKED, not when the
  // closure was built — at that point it was still false.
  const onboardedHere = useCallback(
    () => didOnboardHere.current || hasOrganization,
    [hasOrganization],
  );

  /**
   * Creates the organization and refreshes the Clerk session.
   *
   * Called from the wizard's SETUP step, for both plans — not at finish. Pro cannot collect a card
   * before it: a subscription is billed to the organization's Stripe Customer, so there is nothing
   * to mint a SetupIntent against until the organization exists, which is why onboarding could
   * never have taken a real payment in the order it originally ran. Starter gains the same
   * treatment for free, and one screen owns the wait for both.
   *
   * The session reload matters: the JWT has to carry the new CLIENT_ADMIN membership before any
   * request that resolves `requester.organizationId`, which is every /tier route.
   */
  const ensureOrganization = useCallback(async () => {
    if (!user) throw new Error("Not signed in.");
    if (!formData.termsVersion) throw new Error("The Platform Terms have not been accepted.");
    const result = await onboardUserApi({
      firstName: formData.firstName,
      lastName: formData.lastName,
      organizationName: formData.orgName,
      // The version the profile step displayed, and the wording beside it. The server checks the
      // version is still current and refuses a stale one, so an account is never created against
      // text the client did not read.
      termsVersion: formData.termsVersion,
      termsLabel: termsLabelFor(formData.termsVersion),
    });
    // Only a call that created the organization is a sign_up — an invited user is not.
    if (!result.alreadyOnboarded) markSignUpPending(user.id, signUpMethodFor(user));
    didOnboardHere.current = true;
    markOrganizationCreated();
    // The session reload IS awaited: the next request has to carry the CLIENT_ADMIN membership.
    await user.reload();
    // The cache invalidation is NOT. `invalidateQueries` resolves only when the refetch settles, so
    // awaiting it makes this hook's completion depend on an unrelated query being fast — and while
    // it hangs, the setup step sits on its spinner with nothing to report. Fire and forget,
    // exactly as `handleFinish` below does and for the same reason.
    void queryClient.invalidateQueries({ queryKey: ["me", "permissions"] });
  }, [
    user,
    formData.firstName,
    formData.lastName,
    formData.orgName,
    formData.termsVersion,
    queryClient,
    markOrganizationCreated,
  ]);

  /**
   * `skipInvites` is passed rather than read off `formData`, because the caller that means it —
   * "Skip for now" — would otherwise clear the emails with a state
   * update and call this in the same tick, where the closure still holds the OLD array and sends
   * invitations the client just declined to send.
   */
  const handleFinish = useCallback(
    async (options?: { skipInvites?: boolean }) => {
      if (!user) return;
      setSubmitting(true);
      setError(null);

      const completeBackendOnboarding = async () => {
        if (!formData.termsVersion) throw new Error("The Platform Terms have not been accepted.");
        return await onboardUserApi({
          firstName: formData.firstName,
          lastName: formData.lastName,
          organizationName: formData.orgName,
          termsVersion: formData.termsVersion,
          termsLabel: termsLabelFor(formData.termsVersion),
        });
      };

      const uploadAvatarIfPresent = async () => {
        if (formData.avatarFile) {
          await user.setProfileImage({ file: formData.avatarFile });
        }
      };

      const refreshClerkSession = async () => {
        await user.reload();
      };

      const finalizeAndNavigate = () => {
        clearPersistedState();

        try {
          sessionStorage.setItem(NEW_ACCOUNT_STORAGE_KEY, "1");
        } catch (err) {
          console.warn("[Onboarding] Failed to set session storage:", err);
        }

        // Fire and forget cache invalidation (no need to await and block navigation)
        void queryClient.invalidateQueries({ queryKey: ["me", "permissions"] });

        navigate(POST_ONBOARDING_REDIRECT_URL, { replace: true, state: PAGE_VIEW_STATE });
      };

      try {
        // The setup step already created the organization, so skip the round trip and go straight to
        // the work that is still owed. Only a run that never reached setup — an invited user landing
        // here by mistake — needs to ask the backend anything.
        const result = onboardedHere()
          ? { alreadyOnboarded: true }
          : await completeBackendOnboarding();

        // An invited user who landed on /onboarding — the backend found they already have an org, so
        // there is no wizard work to do. NOT a client we onboarded ourselves a moment ago, who is
        // still owed the avatar and the invitations below.
        if (result.alreadyOnboarded && !onboardedHere()) {
          clearPersistedState();
          navigate(POST_ONBOARDING_REDIRECT_URL, { replace: true, state: PAGE_VIEW_STATE });
          return;
        }

        await uploadAvatarIfPresent();
        // Reload the Clerk session before sending invitations so the updated JWT reflects
        // the CLIENT_ADMIN membership on the new org. Without this, the invite requests
        // would arrive with a stale token and fail the capability check.
        await refreshClerkSession();

        // Send onboarding team invitations (best-effort: individual failures don't block finish).
        // organizationId is intentionally omitted — the backend uses requester.organizationId,
        // which is the org just created above. Role is always Org View Only as specified.
        const validEmails = options?.skipInvites
          ? []
          : formData.invitedEmails.map((e) => e.trim()).filter(Boolean);
        const dupeEmails: string[] = [];
        for (const email of validEmails) {
          try {
            await createUser({
              email,
              // will update it when they accept the invitation and complete sign-up.
              firstName: email.split("@")[0] ?? "",
              role: ROLES.ORG_VIEW_ONLY,
              // No organizationId — resolved server-side from the requester's org.
            });
          } catch (e) {
            const msg = e instanceof Error ? e.message : "";
            if (msg.toLowerCase().includes("already exists")) {
              dupeEmails.push(email);
            } else {
              console.warn(`[Onboarding] Failed to invite ${email}:`, msg);
            }
          }
        }

        // Surface duplicate-email errors as a single, readable warning. This does not
        // prevent navigation — the org is set up and the user is onboarded; the duplicate
        // invitations can be retried from Account → Users.
        if (dupeEmails.length > 0) {
          setError(
            `The following email${
              dupeEmails.length > 1 ? "s are" : " is"
            } already registered and could not be invited: ${dupeEmails.join(", ")}`,
          );
          // Give the user a moment to read the message before navigating.
          await new Promise((r) => setTimeout(r, 3000));
        }

        finalizeAndNavigate();
      } catch (e) {
        setError(userFacingErrorMessage(e));
        setSubmitting(false);
      }
    },
    [
      user,
      formData,
      setSubmitting,
      setError,
      clearPersistedState,
      navigate,
      queryClient,
      onboardedHere,
    ],
  );

  return { handleFinish, ensureOrganization };
}
